import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { checkIn, VISIT_REASONS, VisitReason, getErrorMessage } from '../visitorKioskApi';

// Fontana brand color
const FONTANA_BLUE = '#1a365d';

interface CheckInTabProps {
  onSuccess: () => void;
}

export default function CheckInTab({ onSuccess }: CheckInTabProps) {
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [hostName, setHostName] = useState('');
  const [visitReason, setVisitReason] = useState<VisitReason | ''>('');
  const [otherReasonText, setOtherReasonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setFullName('');
    setCompany('');
    setHostName('');
    setVisitReason('');
    setOtherReasonText('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = fullName.trim();
    if (trimmedName.length < 2) {
      setError('Ange ditt fullständiga namn (minst 2 tecken).');
      return;
    }
    if (!visitReason) {
      setError('Välj en anledning för besöket.');
      return;
    }

    setLoading(true);
    try {
      await checkIn({
        fullName: trimmedName,
        company: company.trim() || undefined,
        hostName: hostName.trim() || undefined,
        visitReason,
        otherReasonText: visitReason === 'Övrigt' ? otherReasonText.trim() : undefined,
      });

      resetForm();
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = {
    '& .MuiInputBase-root': { 
      minHeight: 56,
      borderRadius: 2,
    },
    '& .MuiInputBase-input': { fontSize: '1.05rem' },
    '& .MuiInputLabel-root': { fontSize: '1rem' },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': { borderColor: FONTANA_BLUE },
      '&.Mui-focused fieldset': { borderColor: FONTANA_BLUE },
    },
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar sx={{ bgcolor: '#E8EEF4', width: 64, height: 64, mx: 'auto', mb: 2 }}>
            <HowToRegIcon sx={{ fontSize: 32, color: FONTANA_BLUE }} />
          </Avatar>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Välkommen!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Fyll i dina uppgifter för att checka in
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Row 1: Name + Company */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Fullständigt namn *"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Anna Andersson"
              required
              fullWidth
              inputProps={{ maxLength: 120 }}
              sx={{ flex: 1, ...inputStyles }}
            />
            <TextField
              label="Företag"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Valfritt"
              fullWidth
              inputProps={{ maxLength: 120 }}
              sx={{ flex: 1, ...inputStyles }}
            />
          </Box>

          {/* Row 2: Host + Reason */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Vem besöker du?"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              placeholder="Valfritt"
              fullWidth
              inputProps={{ maxLength: 120 }}
              sx={{ flex: 1, ...inputStyles }}
            />
            <FormControl fullWidth required sx={{ flex: 1 }}>
              <InputLabel>Anledning för besök *</InputLabel>
              <Select
                value={visitReason}
                onChange={(e) => setVisitReason(e.target.value as VisitReason)}
                label="Anledning för besök *"
                sx={{
                  minHeight: 56,
                  borderRadius: 2,
                }}
              >
                {VISIT_REASONS.map((reason) => (
                  <MenuItem key={reason} value={reason} sx={{ py: 1.5 }}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Conditional: Other reason text */}
          {visitReason === 'Övrigt' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Beskriv kort"
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                placeholder="Ange anledning..."
                fullWidth
                inputProps={{ maxLength: 200 }}
                sx={inputStyles}
              />
            </Box>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={22} color="inherit" /> : <PersonAddIcon />}
            sx={{
              minHeight: 56,
              fontSize: '1.1rem',
              fontWeight: 600,
              mt: 2,
              width: '100%',
              borderRadius: 2,
              bgcolor: FONTANA_BLUE,
              '&:hover': { bgcolor: '#0f2744' },
            }}
          >
            {loading ? 'Checkar in...' : 'Checka in'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
          Uppgifterna raderas automatiskt efter utcheckning.
        </Typography>
      </CardContent>
    </Card>
  );
}
